import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Box, LinearProgress, Pagination, TextField } from '@mui/material';
import { Button } from '@material-ui/core';
import Select from 'antd/lib/select';

import { useDebounce } from 'utils/useDebounce';
import DeedsPageBanner from 'assets/pageBanners/deeds.avif';
import { selectUser } from 'entities/user/selectors';
import { ActionVisibilityStatus, useActionTemplateResultsQuery } from 'generated/graphql';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { useCategories } from 'entities/cause/api';

import { ActionTemplatesList } from './ActionTemplatesList';
import 'antd/lib/select/style/index.less';

const { Option } = Select;

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const ValidTemplateStatus = Object.values(ActionVisibilityStatus)
  // legacy status was used to deprecate values in the db
  .filter((status) => status !== ActionVisibilityStatus.Legacy);

const ActionsLibraryPage = (): JSX.Element => {
  const history = useHistory();
  const [page, setPage] = useState(1);

  const user = useSelector(selectUser);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPublic, setPublic] = useState([]);
  const [selectedTemplateStatus, setTemplateStatus] = useState<(typeof ValidTemplateStatus)[]>([]);
  const causes = useCategories();
  const [selectedCauses, setSelectedCauses] = useState<string[]>([]);

  const debouncedSearchTerm = useDebounce(searchTerm);
  const isActionsEnabled =
    user && user.hasFeature(Features.ACTIONS) && user.hasPrivilege(Privileges.ACTIONS_MANAGE);

  if (!isActionsEnabled) {
    history.replace('/');
  }

  const itemsPerPage = 24;
  const { data, loading, error } = useActionTemplateResultsQuery({
    variables: {
      searchTerm: debouncedSearchTerm,
      public: selectedPublic,
      status: selectedTemplateStatus,
      causes: selectedCauses,
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
    },
    fetchPolicy: 'no-cache',
  });
  const actionTemplates = data?.actionTemplateResults?.templates ?? [];

  const FiltersBlock = (
    <Box mb={4} display="flex" alignItems="baseline">
      <TextField
        variant="standard"
        label="Search templates"
        placeholder=""
        value={searchTerm}
        style={{ width: 290 }}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
      />

      <Select
        value={selectedPublic}
        placeholder="Filter by public"
        mode="multiple"
        onChange={(newValue) => {
          setPublic(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        <Option value>Public</Option>
        <Option value={false}>Company Only</Option>
      </Select>
      <Select
        value={selectedCauses}
        placeholder="Filter by causes"
        mode="multiple"
        onChange={(newValue) => {
          setSelectedCauses(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        {causes.map((c) => (
          <Option value={c.id} key={c.id}>
            {c.name}
          </Option>
        ))}
      </Select>
      <Select
        value={selectedTemplateStatus}
        placeholder="Filter by status"
        mode="multiple"
        onChange={(newValue) => {
          setTemplateStatus(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        {ValidTemplateStatus.map((status) => (
          <Option value={status} key={status}>
            {capitalizeFirstLetter(status)}
          </Option>
        ))}
      </Select>
      <Button
        onClick={() => {
          setSearchTerm('');
          setPublic([]);
          setSelectedCauses([]);
          setTemplateStatus([]);
          setPage(1);
        }}
        style={{ marginLeft: 30 }}
      >
        Clear Filters
      </Button>
    </Box>
  );

  const NewActionTemplateButton = (
    <div>
      <Button
        color="primary"
        variant="outlined"
        size="large"
        component={Link}
        to="/actions/template/new"
      >
        New Action Template
      </Button>
    </div>
  );

  return (
    <>
      <Helmet title="Actions library" />
      <PageBannerContainer color="#DDDFFF" backgroundImage={DeedsPageBanner} backgroundSize="500px">
        <h1>Actions Library</h1>

        {NewActionTemplateButton}
      </PageBannerContainer>
      {FiltersBlock}
      {error ? (
        <div>There was an error loading this page. Please try again.</div>
      ) : loading ? (
        <LinearProgress />
      ) : (
        <>
          <ActionTemplatesList actionTemplates={actionTemplates} loading={loading} />
          {(data?.actionTemplateResults?.total ?? 0) > 0 && (
            <Box my={2} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil((data?.actionTemplateResults?.total ?? 0) / itemsPerPage)}
                page={page}
                onChange={(_, pageNumber) => {
                  setPage(pageNumber);
                }}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default ActionsLibraryPage;
